import { usePipelineNodes } from "@stores/data.store";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageStructure, { PageContent, PageContentHeader, PageContentInner} from "@pages/PageStructure.component";
import { NodeList } from "@components/pipelineNodes/PipelineNodeList.component";
import { getGroupValueForNodeType } from "@services/modeling.service";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import { PipelineNode } from "@models/pipelineNode";
import { DraftOnly, ProdOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import InfoAlert from "@components/statusIndicators/InfoAlert.component";
import { useDimensions, useMeasures } from "@models/reportBuilder";


export const ReportsEmptyState = () => {
    const metrics = useMeasures();
    const dimensions = useDimensions();

    const noMetrics = (metrics.data?.length ?? 0) === 0;
    const noDimensions = (dimensions.data?.length ?? 0) === 0;
    
    return <>
        <DraftOnly>
            <div className="empty-state">
                <div className="d-flex flex-column align-items-center">
                    {noMetrics && noDimensions && (
                        <>
                            <h3>Slow down there, partner! To build a report, you’ll need both a metric and a dimension.</h3>
                            <p>Dimensions are attributes you’d like to group or filter by (e.g., ‘Last Name’ or ‘Zip Code’).</p>
                            <p>Metrics are calculations based on your data (e.g., ‘Total Sales’ or ‘Average Order Value’).</p>
                            <p>Head over to the Semantic Layer to:</p>
                                <ul>
                                    <li>Toggle a column as a dimension in entity details.</li>
                                    <li>Create a metric using your entities.</li>
                                </ul>
                            <Link className="btn btn-pliable" to={`/semantic-layer`}>Create one now.</Link>
                        </>
                    )}
                    {!noMetrics && noDimensions && (
                        <>
                            <h3>Great job creating a metric! To build a report, you’ll also need at least one dimension.</h3>
                            <p>Dimensions are attributes you’d like to group or filter by (e.g., ‘Last Name’ or ‘Zip Code’).</p>
                            <p>Head over to the Semantic Layer and toggle a column as a dimension in entity details.</p>
                            <Link className="btn btn-pliable" to={`/semantic-layer`}>Create one now.</Link>
                        </>
                    )}
                    {noMetrics && !noDimensions && (
                        <>
                            <h3>You’re almost there! To build a report, you’ll need at least one metric.</h3>
                            <p>Metrics are calculations based on your data (e.g., ‘Total Sales’ or ‘Average Order Value’).</p>
                            <p>Head over to the Semantic Layer to create a metric using your entities.</p>
                            <Link className="btn btn-pliable" to={`/semantic-layer`}>Create one now.</Link>
                        </>
                    )}
                    {!noMetrics && !noDimensions && (
                        <>
                            <h3>You haven't created any reports yet.</h3>
                            <p>Pliable makes it easy to build accurate reports using your Entities and Metrics, without writing any SQL.</p>
                            <Link className="btn btn-pliable" to={`/wizard/report`}>Create one now.</Link>
                        </>
                    )}
                </div>
            </div>
        </DraftOnly>
        <ProdOnly>
            <h3>You haven't created any reports yet.</h3>
            <p>Pliable makes it easy to build accurate reports using your Entities and Metrics, without writing any SQL.</p>
            <InfoAlert>
                You're currently in Production Mode. You must enter Develop Mode to create reports.
            </InfoAlert>
        </ProdOnly>
    </>
}

const ReportsPage = () => {
    const pipelineNodes = usePipelineNodes();
    const isLoading = pipelineNodes.isLoading;

    const reportNodes = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }

        return pipelineNodes.data.filter((node) => getGroupValueForNodeType(node.node_type) == 'REPORTING').sort((a, b) => a.name.localeCompare(b.name));
    }, [pipelineNodes.dataUpdatedAt]);

    const navigate = useNavigate();
    
    return <PageStructure>
        <PageContent>
        <PageContentHeader>
            <div>
            <DashboardIcon bgColor='success' icon='mdi mdi-table-star'/>
                <div className="flex-1 ms-2">
                    <h1 className="mb-0">Reports</h1>
                    <div className="text-muted font-13">Manage all of your stakeholder-facing reports.</div>
                </div>
                <ReadOnly>
                    <Link className="btn btn-primary" to="/wizard/report">
                        <i className="mdi mdi-plus-thick"></i> Create New Report
                    </Link>
                </ReadOnly>
            </div>
        </PageContentHeader>
        <PageContentInner hasHeader>
            <div className="p-4">
                <div className="card">
                    <div className="card-body">
                        {reportNodes.length == 0 && !isLoading &&  <>
                            <ReportsEmptyState/>
                        </>}   
                        <NodeList nodes={reportNodes} onClick={(node: PipelineNode) => {
                            navigate(`/node/${node.id}/config`);
                        }}/>
                    </div>
                </div>
            </div>
        </PageContentInner>
    </PageContent>
    </PageStructure>
}

export default ReportsPage;